import { createSlice } from "@reduxjs/toolkit";
import type { Story } from "@theirstory/common/api_types/backend";

import type { BaseSlateParagraph, SlateParagraph } from "../../../components/utils/types";
import { metadata2notes, notes2metadata } from "../../../lib/storyNotes";
import type { IndexInterface } from "../../../Pages/Hub/transcript/interfaces";

import { sortIndexesNewestFirst } from "./utils";

export enum AuthorPageLayout {
  FocusOnTranscript = "FocusOnTranscript",
  FocusOnTabs = "FocusOnTabs",
}

export interface IStoryIndexNotes {
  children: {
    text: string;
  }[];
}

export interface ITime {
  start: number;
  end: number | null;
}
export interface IStoryIndexMetadata {
  index: number;
  timecode: string;
  time: ITime;
  title: string;
  synopsis: string;
  notes: string;
  keywords: string;
  lines: Array<{
    index: number;
    line: string;
    empty: boolean;
    timecodes: string[];
    times: ITime[];
    matches: string[][];
    keywords: string[];
  }>;
}

export type StoryIndex = any;
// export type StoryIndex = {
//   created_at: Date;
//   updated_at: Date;
//   title: string;
//   metadata: IStoryIndexMetadata[];
//   notes: SlateParagraph[];
//   bookmarks:
//   chapters:
//   _id: string;
//   url?: string;
//   loaded?: boolean;
//   unsaved?: boolean;
// };

// Some older payloads still send `createdAt`, but the store uses `created_at` internally.
const normalizeIndexCreatedAt = <T extends { created_at?: string; createdAt?: string }>(
  index: T
): T & { created_at?: string } => ({
  ...index,
  created_at: index.created_at ?? index.createdAt,
});

export type MetadataShape = any;

export interface IndexChapter {
  title: string;
  description?: string | null;
  keywords?: string | null;
}

export interface TranscriptVersion {
  date: string;
  author: string;
  author_id: string;
  file: string;
}

export interface Project {
  _id: string;
  backgroundUrl: string;
  created_at: string;
  creator: string;
  defaultProjectForOrganization: boolean;
  description: string;
  displayTitle: boolean;
  migrated: boolean;
  name: string;
  organization: string;
}

export interface Clip {
  getThumbnailURL?: string;
  getArchiveURL?: string;
  getArchiveAudioURL?: string;
  getArchiveExtendedURL?: string;
  created_at: string;
  description: string;
  transcript?: string;
  end: string;
  start: string;
  story: string;
  tags: string;
  title: string;
  user: string;
  _id: string;
  thumbnailURL: string;
  currentUserProjectRole?: string;
  project: Project;
  storyData: Story;
  shared_page: string;
  clip: string;
  duration: string;
}

export interface ClipModal {
  _id?: string;
  videoUrl: string;
  clipFragmentUrl: string;
  clipExtendedFragmentUrl: string;
  description: string;
  transcript?: string;
  start: string | number;
  end: string | number;
  tags: string;
  title: string;
  storyDuration: number;
  isAudioFile: boolean;
  isOpen: boolean;
}

export interface IStoryAuthoringState {
  items: Array<StoryIndex>; // TODO: rename to `indexes` and turn into an object keyed by `_id`
  story: any; // LoadedStory;
  transcriptSlate?: SlateParagraph[];
  playing: boolean;
  showThumbnail?: boolean;
  timecodeSecondsSkip: number;
  progressSeconds: number;
  playbackRate: number;
  showIndex: boolean;
  showTranscript: boolean;
  pauseWhileTyping: boolean;
  autoScroll: boolean;
  duration: number;
  skipCounter: number;
  videoReady: boolean;
  storyLoaded: boolean;
  storyLoadError: boolean;
  unsavedChanges: boolean;
  lastSaved: null | Date;
  isUpdatingPublicPage: boolean;
  localBackupLoaded: boolean;
  selectedText: string;
  clipStart: string;
  clipEnd: string;
  clips: Clip[];
  clipModal: ClipModal | null;
  clipsLength: string;
  isLoadingClips: boolean;
  layout: AuthorPageLayout;
  addBookmarkToNotes: boolean;
  addChapterToNotes: IndexChapter | null;
  versions: TranscriptVersion[];
  versionPreview: {
    isLoading: boolean;
    preview: SlateParagraph[];
  };
  hubPage: {
    loading: boolean;
    search: {
      searchValue: string;
      tagSearch: string;
      totalFound: number;
      currentWordIndex: number;
      currentChapters: any[];
      isSearchActive: boolean;
      semanticChunkTimes: number[];
      semanticChunkEndTimes: number[];
      semanticChunkScores: number[];
    };
    indexes: IndexInterface[];
    selectedIndexId: string | null;
    showOnlySummaries: boolean;
    showIndexes: boolean;
    isAutoScroll: boolean;
    isHighlightWords: boolean;
    hasHighlightedStartToEndWords: boolean;
    paragraphs: BaseSlateParagraph[];
    loadingTranscript: boolean;
    loadedTranscript: boolean;
  };
}

const initialState: IStoryAuthoringState = {
  items: [],
  story: null,
  transcriptSlate: null,
  playing: false,
  showThumbnail: true,
  timecodeSecondsSkip: undefined,
  progressSeconds: 0,
  playbackRate: 1,
  showIndex: true,
  showTranscript: true,
  pauseWhileTyping: false,
  autoScroll: false,
  duration: 0,
  videoReady: false,
  storyLoaded: false,
  storyLoadError: false,
  // In order to signal that a skip happened, even if to the same time
  // Eg: Skipping to 0 multiple times wouldn't trigger the `useEffect`
  // since `timecodeSecondsSkip` doesn't change
  skipCounter: 0,
  unsavedChanges: false,
  lastSaved: null,
  localBackupLoaded: false,
  isUpdatingPublicPage: false,
  selectedText: "",
  clipStart: "",
  clipEnd: "",
  clips: [],
  clipModal: null,
  clipsLength: "",
  isLoadingClips: true,
  layout: AuthorPageLayout.FocusOnTranscript,
  addBookmarkToNotes: false,
  addChapterToNotes: null,
  versions: [],
  versionPreview: {
    isLoading: false,
    preview: [],
  },
  hubPage: {
    search: {
      searchValue: null,
      tagSearch: null,
      totalFound: 0,
      currentWordIndex: 0,
      currentChapters: [],
      isSearchActive: false,
      semanticChunkTimes: [],
      semanticChunkEndTimes: [],
      semanticChunkScores: [],
    },
    loading: false,
    indexes: [],
    selectedIndexId: null,
    paragraphs: [],
    showOnlySummaries: false,
    showIndexes: false,
    isAutoScroll: true,
    isHighlightWords: true,
    hasHighlightedStartToEndWords: false,
    loadingTranscript: false,
    loadedTranscript: false,
  },
};

const storyAuthoringSlice = createSlice({
  name: "authoring",
  initialState,
  reducers: {
    LocalBackupFinishedLoading(state) {
      state.localBackupLoaded = true;
    },
    SetHasUnsavedChanges(state, { payload }: { payload: boolean }) {
      state.unsavedChanges = payload;
      if (payload === false) {
        for (const idx of state.items) {
          idx.unsaved = false;
        }
      }
    },
    setSelectedText(state, { payload }: { payload: string }) {
      state.selectedText = payload;
    },
    SetClipsLength(state, { payload }: { payload: string }) {
      state.clipsLength = payload;
    },
    SetStoryThumbnailURL(state, { payload }: { payload: string }) {
      if (state.story) {
        state.story.thumbnailURL = payload;
      }
    },
    setClipStart(state, { payload }: { payload: string }) {
      state.clipStart = payload;
    },
    setClipEnd(state, { payload }: { payload: string }) {
      state.clipEnd = payload;
    },
    SetLastSaved(state, { payload }: { payload: Date }) {
      state.lastSaved = payload;
    },
    TogglePlaying(state, { payload }: { payload: boolean }) {
      if (state.showThumbnail) {
        state.showThumbnail = false;
      }
      state.playing = payload;
    },
    SetPlaybackRate(state, { payload }: { payload: number }) {
      state.playbackRate = payload;
    },
    DeleteStoryIndex(state, { payload: { index_id: indexId } }) {
      state.items = state.items.filter((item) => item._id !== indexId);
    },
    ToggleThumbnailOrVideo(state, { payload }: { payload: boolean }) {
      state.showThumbnail = payload;
    },
    SetSkipTime(state, { payload }: { payload: number }) {
      const newTimecodeSecondsSkip = Math.min(Math.max(0, payload), state.duration - 1);
      state.timecodeSecondsSkip = newTimecodeSecondsSkip;
      state.progressSeconds = newTimecodeSecondsSkip;
      state.skipCounter += 1;
    },
    StoryLoaded(
      state,
      {
        payload: { story, transcriptSlate, transcriptUpdatedAt, indexes },
      }: {
        payload: {
          story: any; // LoadedStory;
          transcriptSlate?: SlateParagraph[];
          transcriptUpdatedAt?: Date;
          indexes: any[]; // IStoryIndex[];
        };
      }
    ) {
      state.story = story;
      state.transcriptSlate = transcriptSlate;
      state.items = sortIndexesNewestFirst((indexes as unknown as StoryIndex[]).map(normalizeIndexCreatedAt));

      state.storyLoaded = true;
      state.lastSaved = transcriptUpdatedAt || new Date(story.updated_at);
    },
    TranscriptVersionsLoaded(
      state,
      {
        payload: { versions },
      }: {
        payload: {
          versions: TranscriptVersion[];
        };
      }
    ) {
      state.versions = versions;
    },
    TranscriptVersionPreviewLoaded(
      state,
      {
        payload: { preview },
      }: {
        payload: {
          preview: SlateParagraph[];
        };
      }
    ) {
      state.versionPreview = {
        preview,
        isLoading: false,
      };
    },
    TranscriptVersionPreviewLoading(
      state,
      {
        payload: { isLoading },
      }: {
        payload: {
          isLoading: boolean;
        };
      }
    ) {
      state.versionPreview.isLoading = isLoading;
    },
    TranscriptUpdated(
      state,
      {
        payload: { transcriptSlate },
      }: {
        payload: {
          transcriptSlate: SlateParagraph[];
        };
      }
    ) {
      if (transcriptSlate) {
        state.transcriptSlate = transcriptSlate;
      }
    },
    PlaybackProgress(
      state,
      {
        payload: { progressSeconds },
      }: {
        payload: {
          progressSeconds: number;
        };
      }
    ) {
      state.progressSeconds = progressSeconds;
    },
    SetStoryDuration(state, { payload: { duration } }: { payload: { duration: number } }) {
      state.duration = duration;
    },
    SetStoryVideoReady(state, { payload: { ready } }: { payload: { ready: boolean } }) {
      state.videoReady = ready;
    },
    SetIndexNotes(
      state,
      {
        payload: { indexId, notes, isLoad = false },
      }: {
        payload: {
          indexId: string;
          notes: SlateParagraph[];
          isLoad?: boolean;
        };
      }
    ) {
      const arrIdx = state.items.findIndex(({ _id }) => (_id as unknown as string) === indexId);

      if (arrIdx < 0) {
        state.items.push({
          notes,
          metadata: notes2metadata(notes),
          ...(isLoad ? { loaded: true } : {}),
        });
      } else {
        state.items[arrIdx].notes = notes;
        state.items[arrIdx].metadata = notes2metadata(notes);
        if (isLoad) {
          state.items[arrIdx].loaded = true;
        }
      }
    },
    SetIndexParagraph(
      state,
      {
        payload: { indexId, notes, paragraphId },
      }: {
        payload: {
          indexId: string;
          paragraphId: number;
          notes: SlateParagraph;
        };
      }
    ) {
      const addIdx = state.items.findIndex(({ _id }) => (_id as unknown as string) === indexId);

      state.items[addIdx][paragraphId] = notes;
      state.unsavedChanges = true;
    },
    SetStoryIndexMetadataActionCreator(
      state,
      {
        payload: { indexId, metadataIndex, metadataKey, metadataValue },
      }: {
        payload: {
          indexId: string;
          metadataIndex: string;
          metadataKey: string;
          metadataValue: unknown;
        };
      }
    ) {
      const idx = state.items.findIndex(({ _id }) => (_id as unknown as string) === indexId);
      const idx2 = state.items[idx].metadata.findIndex(({ index: index_ }) => index_ === metadataIndex);
      state.items[idx].metadata[idx2][metadataKey] = metadataValue;
      state.items[idx].notes = metadata2notes(state.items[idx].metadata);
      state.unsavedChanges = true;
    },
    AddIndexMetadataItem(
      state,
      {
        payload: { indexId, metadataValue },
      }: {
        payload: {
          indexId: string;
          metadataValue: object;
        };
      }
    ) {
      const idx = state.items.findIndex(({ _id }) => (_id as unknown as string) === indexId);
      const newMetadaItem = {
        ...metadataValue,
        // TODO: check this, what is index used for? This should be a unique id..?
        // index: state.items[idx].metadata.length,
      };
      const newMetadataArray = [...state.items[idx].metadata, newMetadaItem];
      const orderedArray = newMetadataArray.sort((a, b) => {
        if (a.time.start > b.time.start) return 1;
        else return -1;
      });

      state.items[idx].metadata = orderedArray;
      state.items[idx].notes = metadata2notes(state.items[idx].metadata);
      state.unsavedChanges = true;
    },
    EditIndexMetadataItem(
      state,
      {
        payload: { indexId, itemPosition, item },
      }: {
        payload: {
          indexId: string;
          itemPosition: number;
          item: object;
        };
      }
    ) {
      const idx = state.items.findIndex(({ _id }) => (_id as unknown as string) === indexId);
      const metadataArray = [...state.items[idx].metadata];
      if (itemPosition >= metadataArray.length) {
        return;
      }

      metadataArray.splice(itemPosition, 1, item);
      metadataArray.sort((a, b) => {
        if (a.time.start > b.time.start) return 1;
        else return -1;
      });

      state.items[idx].metadata = metadataArray;
      state.items[idx].notes = metadata2notes(state.items[idx].metadata);
      state.unsavedChanges = true;
    },
    DeleteIndexMetadataItem(
      state,
      {
        payload: { indexId, itemPosition },
      }: {
        payload: {
          indexId: string;
          itemPosition: number;
        };
      }
    ) {
      const idx = state.items.findIndex(({ _id }) => (_id as unknown as string) === indexId);
      const metadataArray = [...state.items[idx].metadata];
      if (itemPosition >= metadataArray.length) {
        return;
      }
      metadataArray.splice(itemPosition, 1);
      state.items[idx].metadata = metadataArray;
      state.items[idx].notes = metadata2notes(state.items[idx].metadata);
      state.unsavedChanges = true;
    },
    ToggleAddBookmarkToNotes(state, { payload }: { payload: boolean }) {
      state.addBookmarkToNotes = payload;
    },
    ToggleAddChapterToNotes(state, { payload }: { payload: IndexChapter | null }) {
      state.addChapterToNotes = payload;
    },
    SetStoryIndexTitleActionCreator(
      state,
      {
        payload: { indexId, title },
      }: {
        payload: {
          indexId: string;
          title: string;
        };
      }
    ) {
      const index = state.items.findIndex(({ _id }) => _id === indexId);

      state.items[index].title = title;
      state.unsavedChanges = true;
    },
    ToggleShowIndex(state, { payload }: { payload: boolean }) {
      state.showIndex = payload;
    },
    ToggleShowTranscript(state, { payload }: { payload: boolean }) {
      state.showTranscript = payload;
    },
    TogglePauseWhileTyping(state, { payload }: { payload: boolean }) {
      state.pauseWhileTyping = payload;
    },
    ToggleAutoScroll(state, { payload }: { payload: boolean }) {
      state.autoScroll = payload;
    },
    AddStoryIndex(
      state,
      {
        payload,
      }: {
        payload: StoryIndex;
      }
    ) {
      let index = state.items.findIndex(({ _id }) => _id === payload._id);
      if (index > 0) state.items.splice(index, 1);
      state.items.push(normalizeIndexCreatedAt(payload));
      state.items = sortIndexesNewestFirst(state.items);

      index = state.items.findIndex(({ _id }) => _id === payload._id);
      state.items[index].metadata = notes2metadata(state.items[index].notes);
      state.items[index].unsaved = true;
      state.unsavedChanges = true;
    },
    TogglePanels(state) {
      if (state.layout === AuthorPageLayout.FocusOnTranscript) {
        state.layout = AuthorPageLayout.FocusOnTabs;
      } else {
        state.layout = AuthorPageLayout.FocusOnTranscript;
      }
      // state.clipChanges = true;
    },
    SetHubPageParagraphs(
      state,
      { payload: { paragraphs, indexes } }: { payload: { paragraphs: BaseSlateParagraph[]; indexes: IndexInterface[] } }
    ) {
      state.hubPage.paragraphs = paragraphs;
      state.hubPage.indexes = indexes;
    },
    SetParagraphSearchValue(
      state,
      {
        payload: {
          paragraphs,
          search: { searchValue, totalFound, currentWordIndex },
        },
      }: {
        payload: {
          paragraphs: BaseSlateParagraph[];
          search: { searchValue: string; totalFound: number; currentWordIndex: number };
        };
      }
    ) {
      state.hubPage.paragraphs = paragraphs;
      state.hubPage.search.currentWordIndex = currentWordIndex;
      state.hubPage.search.searchValue = searchValue;
      state.hubPage.search.totalFound = totalFound;
    },
    SetChaptersSearchValue(
      state,
      {
        payload: {
          search: {
            searchValue,
            totalFound,
            currentChapters,
            currentWordIndex,
            semanticChunkTimes,
            semanticChunkEndTimes,
            semanticChunkScores,
          },
        },
      }: {
        payload: {
          search: {
            searchValue: string;
            totalFound: number;
            currentChapters: any[];
            currentWordIndex: number;
            semanticChunkTimes?: number[];
            semanticChunkEndTimes?: number[];
            semanticChunkScores?: number[];
          };
        };
      }
    ) {
      state.hubPage.search.currentWordIndex = currentWordIndex;
      state.hubPage.search.searchValue = searchValue;
      state.hubPage.search.currentChapters = currentChapters;
      state.hubPage.search.totalFound = totalFound;
      if (semanticChunkTimes) {
        state.hubPage.search.semanticChunkTimes = semanticChunkTimes;
      }
      if (semanticChunkEndTimes) {
        state.hubPage.search.semanticChunkEndTimes = semanticChunkEndTimes;
      }
      if (semanticChunkScores) {
        state.hubPage.search.semanticChunkScores = semanticChunkScores;
      }
      // Do NOT automatically set isSearchActive - let it be controlled explicitly
    },
    SetTagSearchValue(
      state,
      {
        payload: {
          search: { tagSearch },
        },
      }: {
        payload: {
          search: { tagSearch: string };
        };
      }
    ) {
      state.hubPage.search.tagSearch = tagSearch;
    },
    SetNewWordIndex(state, { payload: { currentWordIndex } }: { payload: { currentWordIndex: number } }) {
      state.hubPage.search.currentWordIndex = currentWordIndex;
    },
    SetShowIndexes(state, { payload: { value } }: { payload: { value: boolean } }) {
      state.hubPage.showIndexes = value;
    },
    SetShowOnlySummaries(state, { payload }: { payload: boolean }) {
      state.hubPage.showOnlySummaries = payload;
    },
    SetIndexId(state, { payload: { indexId } }: { payload: { indexId: string } }) {
      state.hubPage.selectedIndexId = indexId;
    },
    SetAutoScroll(state, { payload: { value } }: { payload: { value: boolean } }) {
      state.hubPage.isAutoScroll = value;
    },
    SetHighlightWords(state, { payload: { value } }: { payload: { value: boolean } }) {
      state.hubPage.isHighlightWords = value;
    },
    SetHasHighlightedStartToEndWords(state, { payload: { value } }: { payload: { value: boolean } }) {
      state.hubPage.hasHighlightedStartToEndWords = value;
    },
    UpdateStoryClips(
      state,
      {
        payload: { clips },
      }: {
        payload: {
          clips: Clip[];
        };
      }
    ) {
      state.clips = [...clips].sort(
        (a: Clip, b: Clip) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime()
      );
    },
    UpdateClipModal(
      state,
      {
        payload: { clip },
      }: {
        payload: {
          clip: ClipModal;
        };
      }
    ) {
      state.clipModal = clip;
    },
    ResetClipModal(state) {
      state.clipModal = initialState.clipModal;
    },
    SetIsLoadingClips(
      state,
      {
        payload: { isLoading },
      }: {
        payload: {
          isLoading: boolean;
        };
      }
    ) {
      state.isLoadingClips = isLoading;
    },
    SetPublicPageUrl(
      state,
      {
        payload: { url },
      }: {
        payload: {
          url: string;
        };
      }
    ) {
      state.story.shared_page = url;
    },
    SetIsUpdatingPublicPage(
      state,
      {
        payload: { isUpdatingPublicPage },
      }: {
        payload: {
          isUpdatingPublicPage: boolean;
        };
      }
    ) {
      state.story.isUpdatingPublicPage = isUpdatingPublicPage;
    },
    ClearStoryAuthoringState(state) {
      Object.assign(state, initialState);
    },
    SetTranscriptLoadingState(
      state,
      {
        payload: { loadingTranscript, loadedTranscript },
      }: { payload: { loadingTranscript: boolean; loadedTranscript: boolean } }
    ) {
      state.hubPage.loadingTranscript = loadingTranscript;
      state.hubPage.loadedTranscript = loadedTranscript;
    },
    SetIsSearchActive(state, { payload: { value } }: { payload: { value: boolean } }) {
      state.hubPage.search.isSearchActive = value;
    },
  },
  extraReducers: (builder) => {
    builder.addMatcher(
      (action) => action.type === "storyAuthoring/storyAsyncOpsUpdated",
      (state, action: any) => {
        if (state.story) {
          state.story.asyncOperations = action.payload;
        }
      }
    );
    builder.addMatcher(
      (action) => action.type === "story/load/pending",
      (state) => {
        state.storyLoadError = false;
      }
    );
    builder.addMatcher(
      (action) => action.type === "story/load/rejected",
      (state) => {
        state.storyLoadError = true;
      }
    );
  },
});

export const {
  AddStoryIndex,
  StoryLoaded,
  TogglePauseWhileTyping,
  ToggleAutoScroll,
  ToggleShowIndex,
  ToggleShowTranscript,
  SetIndexNotes,
  PlaybackProgress,
  SetStoryDuration,
  SetIndexParagraph,
  TranscriptUpdated,
  TranscriptVersionsLoaded,
  TranscriptVersionPreviewLoaded,
  TranscriptVersionPreviewLoading,
  SetStoryIndexTitleActionCreator,
  SetStoryVideoReady,
  AddIndexMetadataItem,
  EditIndexMetadataItem,
  DeleteIndexMetadataItem,
  SetStoryIndexMetadataActionCreator,
  SetPlaybackRate,
  SetSkipTime,
  TogglePlaying,
  ToggleThumbnailOrVideo,
  SetHasUnsavedChanges,
  SetLastSaved,
  DeleteStoryIndex,
  LocalBackupFinishedLoading,
  setSelectedText,
  SetParagraphSearchValue,
  SetChaptersSearchValue,
  SetTagSearchValue,
  SetHubPageParagraphs,
  SetNewWordIndex,
  SetShowIndexes,
  SetShowOnlySummaries,
  SetIndexId,
  SetAutoScroll,
  SetHighlightWords,
  SetHasHighlightedStartToEndWords,
  setClipStart,
  setClipEnd,
  SetClipsLength,
  SetStoryThumbnailURL,
  TogglePanels,
  ToggleAddBookmarkToNotes,
  ToggleAddChapterToNotes,
  UpdateStoryClips,
  UpdateClipModal,
  SetIsLoadingClips,
  ClearStoryAuthoringState,
  ResetClipModal,
  SetPublicPageUrl,
  SetIsUpdatingPublicPage,
  SetTranscriptLoadingState,
  SetIsSearchActive,
} = storyAuthoringSlice.actions;

export default storyAuthoringSlice.reducer;
